// Listings tab body for the market panel. Phase 4 Step 16.
//
// Dumb-renderer pattern: MarketUI owns all state (selection, price,
// pending flags, listings array) and calls build() with a snapshot +
// callbacks on every rebuild. Scroll offset is the one piece of
// state owned locally so rebuilds don't reset the viewer to the top.
// Full Container.removeChildren + re-add per rebuild is fine at
// Phase 4 scale (tens of listings).
//
// Body layout (top → bottom inside the tab rect):
//   [Listings table, scrolls via mouse wheel]      (flex top)
//   [Divider]
//   [List-an-item form — see market-list-form.ts] (fixed H=FORM_H)

import { Container, Graphics, Text } from "pixi.js";
import type { ActiveListing } from "../../shared/market.ts";
import type { ItemId } from "../../shared/items.ts";
import { getItemDef, items } from "../../shared/items.ts";
import type { InventorySlot, PlayerState } from "../../shared/messages.ts";
import { parseItemId, QUALITY_COLORS, QUALITY_LABELS } from "../../shared/quality.ts";
import { resolveDisplayName } from "./display-name-resolver.ts";
import {
  buildListForm,
  FORM_H,
  type ListFormCallbacks,
  type ListFormSnapshot,
} from "./market-list-form.ts";

// Phase 7 Block D — cancel-policy clarifier. Audit finding 9.4 noted
// that the listing-fee-retention rule wasn't surfaced anywhere in the
// UI; this caption sits next to the player's own listings so it lands
// in-context without per-button hover plumbing. Exported for browser
// test assertions.
export const CANCEL_LISTING_HINT = "Cancelling refunds the item; listing fee is retained.";
import {
  BTN_H,
  BUTTON_PALETTE_BUY,
  BUTTON_PALETTE_CANCEL,
  makeRowButton,
} from "./market-ui-widgets.ts";

const PAD = 12; // contracts:allow-literal — inner padding
const ROW_H = 32; // contracts:allow-literal — listing row height
const ROW_GAP = 4; // contracts:allow-literal
const HEADER_ROW_H = 22; // contracts:allow-literal — column header height
const DIVIDER_H = 1; // contracts:allow-literal
const CANCEL_HINT_H = 22; // contracts:allow-literal — caption strip height
const SWATCH_SZ = 18; // contracts:allow-literal
const BTN_W = 72; // contracts:allow-literal — Buy/Cancel width
const SCROLL_SPEED = 0.6; // contracts:allow-literal — wheel delta multiplier

const TABLE_BG = 0x0e0e22;
const TABLE_BORDER = 0x223366;
const ROW_BG = 0x1a1a3a;
const ROW_BG_ALT = 0x151530;
const ROW_BORDER = 0x2a2a55;
const TEXT_BRIGHT = 0xeeeeff;
const TEXT_MUTED = 0x8888aa;
const TEXT_PLACEHOLDER = 0x556688;
const BORDER_DEFAULT = 0x555555;
const BODY_SIZE = 11; // contracts:allow-literal

export type ListingsTabCallbacks = ListFormCallbacks & {
  onBuy: (listingId: string) => void;
  onCancel: (listingId: string) => void;
};

export type ListingsTabSnapshot = ListFormSnapshot & {
  listings: readonly ActiveListing[];
  selfId: string | null;
  pendingListingIds: ReadonlySet<string>;
  // Phase 7 Block B — name resolution sources. Self carries the local
  // player's display_name; players is the zone-roster cache populated
  // from playerJoined / snapshot. Listings whose seller is offline /
  // cross-zone fall back to shortenId.
  selfPlayer: PlayerState | null;
  zonePlayers: Map<string, PlayerState>;
};

export class MarketListingsTab {
  private container: Container | null = null;
  private listingsScrollY = 0;
  private listingsMaxScroll = 0;

  build(
    parent: Container,
    x: number,
    y: number,
    w: number,
    h: number,
    snap: ListingsTabSnapshot,
    cb: ListingsTabCallbacks,
  ): void {
    this.teardown();
    const root = new Container();
    root.x = x;
    root.y = y;
    parent.addChild(root);
    this.container = root;

    const hasOwnListing = snap.selfId !== null && snap.listings.some((l) => l.sellerId === snap.selfId);
    const hintH = hasOwnListing ? CANCEL_HINT_H : 0;

    const formY = h - FORM_H;
    const hintY = formY - DIVIDER_H - hintH;
    const tableH = (hasOwnListing ? hintY : formY) - DIVIDER_H;
    this.buildListingsTable(root, 0, 0, w, tableH, snap, cb);
    if (hasOwnListing) {
      root.addChild(new Graphics().rect(0, hintY - DIVIDER_H, w, DIVIDER_H).fill({ color: TABLE_BORDER }));
      this.buildCancelHint(root, hintY, w, hintH);
    }
    root.addChild(
      new Graphics().rect(0, formY - DIVIDER_H, w, DIVIDER_H).fill({ color: TABLE_BORDER }),
    );
    buildListForm(root, 0, formY, w, snap, cb);
  }

  // Phase 7 Block D — small caption under the listings table (only when
  // the player has at least one of their own listings showing) clarifying
  // the cancel-vs-fee policy. Lives inline rather than as a hover tooltip
  // so it can't be missed.
  private buildCancelHint(parent: Container, y: number, w: number, h: number): void {
    const hint = new Text({
      text: CANCEL_LISTING_HINT,
      style: { fontSize: BODY_SIZE, fill: TEXT_MUTED, fontFamily: "monospace", fontStyle: "italic" },
    });
    hint.x = PAD;
    hint.y = y + Math.round((h - hint.height) / 2); // contracts:allow-literal
    parent.addChild(hint);
    void w; // reserved for right-aligned variants
  }

  teardown(): void {
    if (this.container) {
      this.container.parent?.removeChild(this.container);
      this.container.destroy({ children: true });
      this.container = null;
    }
  }

  private buildListingsTable(
    parent: Container,
    x: number,
    y: number,
    w: number,
    h: number,
    snap: ListingsTabSnapshot,
    cb: ListingsTabCallbacks,
  ): void {
    const table = new Container();
    table.x = x;
    table.y = y;
    parent.addChild(table);
    table.addChild(
      new Graphics().rect(0, 0, w, h).fill({ color: TABLE_BG }).stroke({ color: TABLE_BORDER, width: 1 }), // contracts:allow-literal
    );

    table.addChild(this.buildColumnHeaders(w));

    const viewportY = HEADER_ROW_H;
    const viewportH = h - HEADER_ROW_H;
    const viewport = new Container();
    viewport.y = viewportY;
    table.addChild(viewport);

    const mask = new Graphics().rect(0, 0, w, viewportH).fill({ color: 0xffffff }); // contracts:allow-literal — mask fill
    mask.y = viewportY;
    table.addChild(mask);
    viewport.mask = mask;

    const inner = new Container();
    viewport.addChild(inner);

    let rowY = 0;
    let index = 0;
    for (const listing of snap.listings) {
      const row = this.buildListingRow(w, listing, snap, cb, index % 2 === 1); // contracts:allow-literal — alt row shading
      row.y = rowY;
      inner.addChild(row);
      rowY += ROW_H + ROW_GAP;
      index++;
    }

    if (snap.listings.length === 0) {
      const empty = new Text({
        text: "No active listings. Be the first to list!",
        style: { fontSize: BODY_SIZE, fill: TEXT_PLACEHOLDER, fontFamily: "monospace", fontStyle: "italic" },
      });
      empty.x = Math.round((w - empty.width) / 2); // contracts:allow-literal — centering idiom
      empty.y = Math.round((viewportH - empty.height) / 2); // contracts:allow-literal — centering idiom
      inner.addChild(empty);
    }

    const contentH = Math.max(0, rowY - ROW_GAP);
    this.listingsMaxScroll = Math.max(0, contentH - viewportH);
    this.listingsScrollY = Math.min(this.listingsScrollY, this.listingsMaxScroll);
    inner.y = -this.listingsScrollY;

    viewport.eventMode = "static";
    viewport.on("wheel", (e) => {
      if (this.listingsMaxScroll <= 0) return;
      const next = Math.max(0, Math.min(this.listingsMaxScroll, this.listingsScrollY + e.deltaY * SCROLL_SPEED));
      if (next === this.listingsScrollY) return;
      this.listingsScrollY = next;
      inner.y = -this.listingsScrollY;
    });
  }

  private buildColumnHeaders(w: number): Container {
    const header = new Container();
    header.addChild(new Graphics().rect(0, 0, w, HEADER_ROW_H).fill({ color: ROW_BG_ALT }));
    const cols = columnLayout(w);
    const labels: Array<[keyof typeof cols, string]> = [
      ["seller", "Seller"],
      ["item", "Item"],
      ["price", "Price"],
      ["action", ""],
    ];
    for (const [key, text] of labels) {
      const t = new Text({
        text,
        style: { fontSize: BODY_SIZE, fill: TEXT_MUTED, fontFamily: "monospace", fontWeight: "bold" },
      });
      t.x = cols[key].x + PAD / 2; // contracts:allow-literal — half-pad for header
      t.y = Math.round((HEADER_ROW_H - t.height) / 2); // contracts:allow-literal
      header.addChild(t);
    }
    return header;
  }

  private buildListingRow(
    w: number,
    listing: ActiveListing,
    snap: ListingsTabSnapshot,
    cb: ListingsTabCallbacks,
    alt: boolean,
  ): Container {
    const row = new Container();
    row.addChild(
      new Graphics()
        .rect(0, 0, w, ROW_H)
        .fill({ color: alt ? ROW_BG_ALT : ROW_BG })
        .stroke({ color: ROW_BORDER, width: 1 }), // contracts:allow-literal
    );

    const cols = columnLayout(w);

    const sellerText = new Text({
      text: resolveDisplayName(listing.sellerId, { self: snap.selfPlayer, players: snap.zonePlayers }),
      style: { fontSize: BODY_SIZE, fill: TEXT_BRIGHT, fontFamily: "monospace" },
    });
    sellerText.x = cols.seller.x + PAD / 2; // contracts:allow-literal
    sellerText.y = Math.round((ROW_H - sellerText.height) / 2); // contracts:allow-literal
    row.addChild(sellerText);

    const itemCell = new Container();
    itemCell.x = cols.item.x + PAD / 2; // contracts:allow-literal
    itemCell.y = Math.round((ROW_H - SWATCH_SZ) / 2); // contracts:allow-literal
    row.addChild(itemCell);

    const itemDef = getItemDef(listing.itemId) ?? items.copper;
    const { quality } = parseItemId(listing.itemId);
    const swatchBorder = quality ? QUALITY_COLORS[quality] : BORDER_DEFAULT;
    const swatchBorderW = quality ? 2 : 1; // contracts:allow-literal
    itemCell.addChild(
      new Graphics()
        .roundRect(0, 0, SWATCH_SZ, SWATCH_SZ, 3) // contracts:allow-literal — corner radius
        .fill({ color: itemDef.color })
        .stroke({ color: swatchBorder, width: swatchBorderW }),
    );
    const nameLabel = quality ? `${QUALITY_LABELS[quality]} ${itemDef.name}` : itemDef.name;
    const nameText = new Text({
      text: nameLabel,
      style: { fontSize: BODY_SIZE, fill: TEXT_BRIGHT, fontFamily: "monospace" },
    });
    nameText.x = SWATCH_SZ + 6; // contracts:allow-literal — gap
    nameText.y = Math.round((SWATCH_SZ - nameText.height) / 2); // contracts:allow-literal
    itemCell.addChild(nameText);

    const priceText = new Text({
      text: `\u20B5 ${listing.price}`,
      style: { fontSize: BODY_SIZE, fill: TEXT_BRIGHT, fontFamily: "monospace", fontWeight: "bold" },
    });
    priceText.x = cols.price.x + PAD / 2; // contracts:allow-literal
    priceText.y = Math.round((ROW_H - priceText.height) / 2); // contracts:allow-literal
    row.addChild(priceText);

    const isOwn = snap.selfId !== null && listing.sellerId === snap.selfId;
    const pending = snap.pendingListingIds.has(listing.listingId);
    const btn = makeRowButton({
      label: isOwn ? "Cancel" : "Buy",
      palette: isOwn ? BUTTON_PALETTE_CANCEL : BUTTON_PALETTE_BUY,
      disabled: pending,
      width: BTN_W,
      onClick: () => {
        if (isOwn) cb.onCancel(listing.listingId);
        else cb.onBuy(listing.listingId);
      },
      overrideLabel: pending ? (isOwn ? "Cancelling…" : "Buying…") : undefined,
    });
    btn.x = cols.action.x + cols.action.w - BTN_W - PAD / 2; // contracts:allow-literal
    btn.y = Math.round((ROW_H - BTN_H) / 2); // contracts:allow-literal
    row.addChild(btn);

    return row;
  }
}

// Column ratios for the listings table. Adjusted once so both the
// header and the per-row renderer share the same layout.
function columnLayout(w: number): {
  seller: { x: number; w: number };
  item: { x: number; w: number };
  price: { x: number; w: number };
  action: { x: number; w: number };
} {
  const sellerW = Math.round(w * 0.22); // contracts:allow-literal — column ratio
  const itemW = Math.round(w * 0.42); // contracts:allow-literal — column ratio
  const priceW = Math.round(w * 0.16); // contracts:allow-literal — column ratio
  const actionW = w - sellerW - itemW - priceW;
  return {
    seller: { x: 0, w: sellerW },
    item: { x: sellerW, w: itemW },
    price: { x: sellerW + itemW, w: priceW },
    action: { x: sellerW + itemW + priceW, w: actionW },
  };
}

// Referenced by callers passing ItemId/InventorySlot in their snapshots.
export type { ItemId, InventorySlot };
